/**
 * Stop-detection on a GPS trail.
 *
 * Walks the (timestamp-ascending) `points` array and groups consecutive
 * points that stay within `radiusMeters` of each other for at least
 * `minDwellMinutes`. Returns an array of stop objects:
 *   { lat, lng, startedAt, endedAt, durationMinutes, count }
 *
 * Designed for sparse field-sales GPS pings (a point every couple of
 * minutes). No external deps — Haversine in plain JS.
 */
const R_EARTH_M = 6_371_000;

const haversineMeters = (a, b) => {
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R_EARTH_M * Math.asin(Math.sqrt(s));
};

export const totalDistanceKm = (points) => {
  if (!points || points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += haversineMeters(points[i - 1], points[i]);
  }
  return total / 1000;
};

export const detectStops = (
  points,
  { radiusMeters = 60, minDwellMinutes = 5 } = {}
) => {
  if (!Array.isArray(points) || points.length === 0) return [];
  const stops = [];
  let cluster = [points[0]];

  const closeCluster = () => {
    if (cluster.length < 2) return;
    const start = new Date(cluster[0].timestamp);
    const end = new Date(cluster[cluster.length - 1].timestamp);
    const durationMin = (end.getTime() - start.getTime()) / 60000;
    if (durationMin < minDwellMinutes) return;
    const avg = cluster.reduce(
      (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
      { lat: 0, lng: 0 }
    );
    stops.push({
      lat: avg.lat / cluster.length,
      lng: avg.lng / cluster.length,
      startedAt: cluster[0].timestamp,
      endedAt: cluster[cluster.length - 1].timestamp,
      durationMinutes: Math.round(durationMin),
      count: cluster.length,
    });
  };

  for (let i = 1; i < points.length; i += 1) {
    const ref = cluster[0];
    const next = points[i];
    if (haversineMeters(ref, next) <= radiusMeters) {
      cluster.push(next);
    } else {
      closeCluster();
      cluster = [next];
    }
  }
  closeCluster();
  return stops;
};
